import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { EeErPensionMethodEnum } from '@nxsys/constants/AppEnums';
import AppSelect from '@nxsys/components/AppSelect';
import { Form, Row, Col } from 'antd';
import { useImperativeHandle, Ref } from 'react';
import { Option } from '@nxsys/types/models/Common';
import { privatePensionSchemeTypeOptions } from '@nxsys/constants/AppConst';
import { getTypeOfValueOptions } from '@nxsys/constants/AppConstants';
import { showCurrencyByCountry } from '@nxsys/utils';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';
import { privatePensionValueRule } from '@nxsys/utils/formFieldValidation';

export type PrivatePensionFormRef = {
  submit: () => void;
  reset: () => void;
};

export type PrivatePensionFormValues = {
  name: string;
  reference: string;
  type: Option<string> | null;
  eePensionValue: number;
  eePensionType: Option<string> | null;
  erPensionValue: number;
  erPensionType: Option<string> | null;
  avcPensionValue: number;
  avcPensionType: Option<string> | null;
};

type PrivatePensionFormProps = {
  formRef?: Ref<PrivatePensionFormRef>;
  onSubmit?: (values: PrivatePensionFormValues) => void;
  defaultValues?: Partial<PrivatePensionFormValues>;
};

const PrivatePensionForm: React.FC<PrivatePensionFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const erPensionType = Form.useWatch('erPensionType', form);
  const eePensionType = Form.useWatch('eePensionType', form);
  const avcPensionType = Form.useWatch('avcPensionType', form);
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const pensionTypeOptions = getTypeOfValueOptions(
    showCurrencyByCountry(payrollCountry),
  );

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
    reset: () => {
      form.resetFields();
    },
  }));

  const onFinish = (values: any) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      initialValues={
        defaultValues || {
          eePensionType: pensionTypeOptions[0],
          eePensionValue: 0,
          erPensionType: pensionTypeOptions[0],
          erPensionValue: 0,
          avcPensionType: pensionTypeOptions[0],
          avcPensionValue: 0,
        }
      }
    >
      <Row gutter={24}>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.name']}
            name="name"
            rules={[{ required: true, message: messages['common.nameIsRequired'] }]}
          >
            <AppInput placeholder={messages['common.enterName']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.typeOfScheme']}
            name="type"
            rules={[{ required: true, message: messages['common.typeIsRequired'] }]}
          >
            <AppSelect
              placeholder={messages['common.selectType']}
              options={privatePensionSchemeTypeOptions}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.referencePolicyNumber']}
            name="reference"
          >
            <AppInput placeholder={messages['common.enterReference']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.defaultEmployerContribution']}
          >
            <Row gutter={8}>
              <Col span={6}>
                <Form.Item name="erPensionType" noStyle>
                  <AppSelect
                    options={pensionTypeOptions}
                    className="w-full !min-w-0"
                  />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Form.Item
                  name="erPensionValue"
                  noStyle
                  dependencies={['erPensionType']}
                  rules={[privatePensionValueRule('erPensionType', messages)]}
                >
                  {erPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                    <AppCurrencyInput placeholder={messages['common.enterAmount']} />
                  ) : (
                    <AppRateInput placeholder={messages['common.enterAmount']} />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.defaultEmployeeContribution']}
          >
            <Row gutter={8}>
              <Col span={6}>
                <Form.Item name="eePensionType" noStyle>
                  <AppSelect
                    options={pensionTypeOptions}
                    className="w-full !min-w-0"
                  />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Form.Item
                  name="eePensionValue"
                  noStyle
                  dependencies={['eePensionType']}
                  rules={[privatePensionValueRule('eePensionType', messages)]}
                >
                  {eePensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                    <AppCurrencyInput placeholder={messages['common.enterAmount']} />
                  ) : (
                    <AppRateInput placeholder={messages['common.enterAmount']} />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.defaultAvcContribution']}
          >
            <Row gutter={8}>
              <Col span={6}>
                <Form.Item name="avcPensionType" noStyle>
                  <AppSelect
                    options={getTypeOfValueOptions(
                      showCurrencyByCountry(payrollCountry),
                    )}
                    className="w-full !min-w-0"
                  />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Form.Item
                  name="avcPensionValue"
                  noStyle
                  dependencies={['avcPensionType']}
                  rules={[privatePensionValueRule('avcPensionType', messages)]}
                >
                  {avcPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                    <AppCurrencyInput placeholder={messages['common.enterAmount']} />
                  ) : (
                    <AppRateInput placeholder={messages['common.enterAmount']} />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default PrivatePensionForm;
